import { emitHealthRecord } from '../../sentryConsole';
import type { MainToWorker, FramePacket } from './protocol';
import { workContract } from './workContracts';
import {
	HEALTH_INTERVAL_MS,
	WorkHealth,
	type WorkCategory,
	type WorkSignal,
	type WorkToken,
	type InputQueueSnapshot,
	type WorkEnd
} from './workHealth';

type Project = {
	generation: number;
	loaded: boolean;
	input?: () => InputQueueSnapshot;
	documentId: string | null;
	presented: number;
	presentations: Map<WorkCategory, WorkSignal>;
	gestures: Map<WorkCategory, { id: number; key: string | null; at: number }>;
};
type Group = { count: number; latest: WorkToken; failed: boolean };

// Existing command arguments carrying the control's coalesce ID.
const COALESCE_ARGUMENT: Readonly<Partial<Record<string, number>>> = {
	edit: 1,
	setValue: 2,
	setValueLocal: 2,
	setParamsBatch: 2,
	setParamsBatchSilent: 2
};

/** Owns the only health timer. No timer or engine queries run on healthy frames. */
export class PageWorkHealth {
	readonly observer: WorkHealth = new WorkHealth(emitHealthRecord);
	private projects: Map<number, Project> = new Map();
	private groups: Map<string, Group> = new Map();
	private nextGesture: number = 0;
	private timer: ReturnType<typeof setInterval> | null = null;
	constructor(private readonly sendProbe: (id: number) => void) {
		this.register(0);
	}
	register(projectId: number, documentId: string | null = null): void {
		const project: Project | undefined = this.projects.get(projectId);
		if (project) {
			project.documentId = documentId;
			this.observer.register(projectId, project.generation, documentId);
			return;
		}
		this.projects.set(projectId, {
			generation: 1,
			loaded: false,
			documentId,
			presented: 0,
			presentations: new Map(),
			gestures: new Map()
		});
		this.observer.register(projectId, 1, documentId);
	}
	inputProbe(projectId: number, probe: () => InputQueueSnapshot): void {
		const project = this.projects.get(projectId);
		if (project) project.input = probe;
	}
	private sampleInput(): void {
		for (const [projectId, project] of this.projects)
			if (project.input) this.observer.input(projectId, project.input());
	}

	generation(projectId: number): number {
		return this.projects.get(projectId)?.generation ?? 0;
	}
	replace(projectId: number, reason: WorkEnd = 'document_replaced'): void {
		const project: Project | undefined = this.projects.get(projectId);
		if (!project) return;
		this.observer.end(projectId, reason);
		project.generation++;
		project.presentations.clear();
		project.presented = 0;
		this.observer.register(projectId, project.generation, project.documentId);
		for (const [key, group] of this.groups)
			if (group.latest.projectId === projectId) this.groups.delete(key);
	}
	prepare(message: MainToWorker, ownerId?: number): MainToWorker {
		if (message.health) return message;
		if (message.op === 'cmd' && message.method === 'endLocalTransaction') {
			this.projects.get(message.projectId)?.gestures.delete('parameter');
			this.projects.get(message.projectId)?.gestures.delete('graph');
		}
		const contract = workContract(message);
		if (!contract) return message;
		const projectId: number = ownerId ?? ('projectId' in message ? message.projectId : 0);
		if (!this.projects.has(projectId)) return message;
		if (contract.requirement === 'load') {
			const project: Project = this.projects.get(projectId)!;
			if (project.loaded) this.replace(projectId);
			project.loaded = true;
		}
		const health: WorkToken = this.observer.begin(
			projectId,
			contract.category,
			'input_preparation_pending',
			this.activityFor(message, projectId, contract.category),
			contract.requirement !== 'result'
		);
		this.arm();
		return { ...message, health };
	}
	interaction(projectId: number, category: WorkCategory, active: boolean): void {
		const gestures = this.projects.get(projectId)?.gestures;
		if (!gestures) return;
		if (active)
			gestures.set(category, { id: ++this.nextGesture, key: null, at: performance.now() });
		else gestures.delete(category);
	}
	private activityFor(
		message: MainToWorker,
		projectId: number,
		category: WorkCategory
	): boolean | number {
		const gestures = this.projects.get(projectId)!.gestures;
		if (
			message.op === 'scheduleRender' ||
			message.op === 'renderFullDocument' ||
			message.op === 'acquireProject'
		)
			return false;
		if (
			message.op === 'cmd' &&
			['editorGraphChanged', 'setOutputTrayLayout'].includes(message.method)
		)
			return false;
		if (message.op === 'editorPointer' || message.op === 'outputTrayPointer') {
			if (message.ev.phase === 0 || !gestures.has(category))
				this.interaction(projectId, category, true);
			const id: number = gestures.get(category)!.id;
			if (message.ev.phase === 2 || message.ev.phase === 3)
				this.interaction(projectId, category, false);
			return id;
		}
		if (message.op === 'cmd') {
			const index: number | undefined = COALESCE_ARGUMENT[message.method];
			const key: unknown = index === undefined ? undefined : message.args[index];
			if (typeof key === 'string') {
				if (gestures.get(category)?.key !== key)
					gestures.set(category, { id: ++this.nextGesture, key, at: performance.now() });
				return gestures.get(category)!.id;
			}
		}
		if (message.op === 'editorWheel' || message.op === 'editorNavigate') {
			const prior = gestures.get(category);
			if (!prior || performance.now() - prior.at > 200) this.interaction(projectId, category, true);
			gestures.get(category)!.at = performance.now();
			return gestures.get(category)!.id;
		}
		return gestures.get(category)?.id ?? true;
	}

	dispatched(message: MainToWorker): void {
		if (message.health)
			this.observer.dispatched(
				message.health,
				message.op === 'query' || message.op === 'moduleQuery' || message.op === 'surfaceScreenshot'
					? 'query_result_pending'
					: 'worker_operation_pending'
			);
		this.arm();
	}
	queryStarted(message: MainToWorker): void {
		const token: WorkToken | undefined = message.health;
		if (!token || workContract(message)?.requirement !== 'result') return;
		this.addGroup(token);
	}
	queryFinished(message: MainToWorker, error?: Error, expected: boolean = false): void {
		const token: WorkToken | undefined = message.health;
		if (!token) return;
		if (error && !expected) this.failure(token, error);
		if (workContract(message)?.requirement === 'result')
			this.finishGroup(token, error !== undefined && !expected);
		else if (error) this.observer.complete(token, true);
		this.arm();
	}
	/** Observe page preparation/upload/fetch from the moment the user commits.
	 * Callers mark validation/cancellation as a handled outcome explicitly. */
	async task<T>(
		projectId: number,
		category: WorkCategory,
		run: () => Promise<T>,
		expectedRejection: boolean = false
	): Promise<T> {
		if (!this.projects.has(projectId)) return run();
		const token: WorkToken = this.observer.begin(projectId, category);
		this.addGroup(token);
		this.arm();
		let failed: boolean = false;
		try {
			return await run();
		} catch (error: unknown) {
			failed = !expectedRejection;
			if (failed) this.failure(token, error);
			throw error;
		} finally {
			this.finishGroup(token, failed);
			this.arm();
		}
	}
	private addGroup(token: WorkToken): void {
		const key: string = this.key(token);
		const group: Group | undefined = this.groups.get(key);
		if (group) {
			group.count++;
			if (token.sequence > group.latest.sequence) group.latest = token;
		} else this.groups.set(key, { count: 1, latest: token, failed: false });
	}
	private finishGroup(token: WorkToken, failed: boolean): void {
		const key: string = this.key(token);
		const group: Group | undefined = this.groups.get(key);
		if (!group || this.generation(token.projectId) !== token.generation) return;
		group.failed ||= failed;
		group.count--;
		if (group.count === 0) {
			this.groups.delete(key);
			this.observer.complete(group.latest, group.failed);
		} // Another query finishing cannot prove progress of this group's oldest unresolved query.
	}
	private key(token: WorkToken): string {
		return `${token.projectId}:${token.generation}:${token.category}`;
	}
	failure(token: WorkToken, error: unknown): void {
		if (this.generation(token.projectId) !== token.generation) return;
		this.sampleInput();
		this.observer.failure(token.projectId, token.category, 'worker_operation_pending', error);
		this.arm();
	}
	receive(signal: WorkSignal): void {
		const project: Project | undefined = this.projects.get(signal.token.projectId);
		if (!project || project.generation !== signal.token.generation) return;
		if (
			signal.done &&
			!signal.failed &&
			signal.presentation &&
			project.presented < signal.presentation
		) {
			project.presentations.set(signal.token.category, signal);
			this.observer.progress(signal.token, 'presentation_pending', signal.progress);
		} else this.observer.signal(signal);
		this.arm();
	}
	presented(projectId: number, sequence: number): void {
		const project: Project | undefined = this.projects.get(projectId);
		if (!project) return;
		project.presented = Math.max(project.presented, sequence);
		for (const [category, signal] of project.presentations)
			if (signal.presentation! <= sequence) {
				project.presentations.delete(category);
				this.observer.signal(signal);
			}
	}
	frame(projectId: number, packet: FramePacket): void {
		if (packet.healthSnapshot)
			this.observer.snapshot(
				projectId,
				packet.healthSnapshot.generation,
				packet.healthSnapshot.state
			);
		if (packet.healthHeld !== undefined) this.observer.hold(projectId, packet.healthHeld);
		for (const signal of packet.health ?? []) this.receive(signal);
		const animation = packet.healthAnimation;
		if (animation && animation.generation === this.generation(projectId))
			this.observer.ongoing(projectId, animation.completed, animation.active);
		this.arm();
	}
	setVisible(visible: boolean): void {
		this.observer.setVisible(visible);
		this.arm();
	}
	end(projectId: number, reason: WorkEnd): void {
		this.observer.end(projectId, reason);
		this.projects.delete(projectId);
		for (const [key, group] of this.groups)
			if (group.latest.projectId === projectId) this.groups.delete(key);
		this.arm();
	}
	endAll(reason: WorkEnd): void {
		for (const projectId of [...this.projects.keys()]) this.end(projectId, reason);
	}
	accountChanged(): void {
		for (const projectId of this.projects.keys()) this.replace(projectId, 'account_changed');
	}
	private arm(): void {
		if (this.observer.active && this.timer === null)
			this.timer = setInterval(() => {
				this.sampleInput();
				this.observer.tick(this.sendProbe);
				this.arm();
			}, HEALTH_INTERVAL_MS);
		else if (!this.observer.active && this.timer !== null) {
			clearInterval(this.timer);
			this.timer = null;
		}
	}
}
